import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { defaultBrief } from "../data/briefs/_default";
import { naturesNestBrief } from "../data/briefs/natures-nest";
import Section from "./Section";
import ProgressThread from "./ProgressThread";
import useAutosave from "./useAutosave";
import usePdf from "./usePdf";
import "./brief.css";

const briefConfigs = {
  "natures-nest": naturesNestBrief
};

export default function BriefPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const config = briefConfigs[slug];
  
  const [currentSection, setCurrentSection] = useState(0);
  const [status, setStatus] = useState("in-progress"); // in-progress, submitted, invalid, expired
  const [validationErrors, setValidationErrors] = useState({});
  const [showResumePrompt, setShowResumePrompt] = useState(false);

  const { data, updateData, isSaving, lastSaved, resumeFromFirestore, saveToFirestore } = useAutosave(slug, {
    answers: {},
    currentSection: 0,
    status: "in-progress"
  });

  const { generatePdf } = usePdf();

  useEffect(() => {
    if (!config) {
      setStatus("invalid");
      return;
    }
    
    // Check for existing progress
    const checkProgress = async () => {
      const remoteData = await resumeFromFirestore();
      if (remoteData && remoteData.status === "in-progress" && Object.keys(remoteData.answers).length > 0) {
        setShowResumePrompt(true);
      }
    };
    checkProgress();
  }, [slug, config]);

  useEffect(() => {
    if (data.currentSection !== undefined) {
      setCurrentSection(data.currentSection);
    }
    if (data.status === "submitted") {
      setStatus("submitted");
    }
  }, [data.currentSection, data.status]);

  if (status === "invalid") {
    return (
      <div className="brief-error-page">
        <h2>Link not active</h2>
        <p>This brief link isn't active. Message us on WhatsApp.</p>
        <a href="https://wa.me/91XXXXXXXXXX" className="brief-btn brief-btn-primary">WhatsApp Us</a>
      </div>
    );
  }

  const sections = defaultBrief.sections;
  const currentSectionData = sections[currentSection];

  const handleNext = () => {
    // Soft validation
    const errors = {};
    currentSectionData.questions.forEach(q => {
      if (q.required && (!data.answers[q.id] || (Array.isArray(data.answers[q.id]) && data.answers[q.id].length === 0))) {
        errors[q.id] = "We'd love to hear your thoughts on this one before moving on.";
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setValidationErrors({});
    if (currentSection < sections.length - 1) {
      const nextIdx = currentSection + 1;
      setCurrentSection(nextIdx);
      updateData("currentSection", nextIdx);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      const prevIdx = currentSection - 1;
      setCurrentSection(prevIdx);
      updateData("currentSection", prevIdx);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    const finalData = {
      ...data,
      clientName: config.brandName || slug,
      status: "submitted",
      submittedAt: new Date().toISOString()
    };
    await saveToFirestore(finalData, true);
    setStatus("submitted");
    updateData("status", "submitted");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (status === "submitted") {
    return (
      <div className="brief-container">
        <ProgressThread current={sections.length - 1} total={sections.length} />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="brief-confirmation"
        >
          <h2>The brief is in.</h2>
          <p>We're already thinking.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <button 
              className="brief-btn brief-btn-primary" 
              onClick={() => generatePdf(defaultBrief, data.answers, config.brandName)}
            >
              Download a copy of your responses
            </button>
            <button 
              className="brief-footer-link" 
              onClick={() => {
                setStatus("in-progress");
                updateData("status", "in-progress");
                setCurrentSection(0);
                updateData("currentSection", 0);
              }}
            >
              Wait, I need to edit something
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="brief-container">
      <ProgressThread current={currentSection} total={sections.length} />
      
      <AnimatePresence mode="wait">
        {showResumePrompt ? (
          <motion.div 
            key="resume"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="brief-intro"
          >
            <h1>Welcome back.</h1>
            <p>We saved your progress. Pick up where you left off?</p>
            <div style={{ marginTop: 32, display: 'flex', gap: 16 }}>
              <button className="brief-btn brief-btn-primary" onClick={() => setShowResumePrompt(false)}>
                Resume
              </button>
              <button className="brief-btn brief-btn-secondary" onClick={() => {
                updateData("answers", {});
                updateData("currentSection", 0);
                setShowResumePrompt(false);
              }}>
                Start over
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {currentSection === 0 && (
              <div className="brief-intro">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {config.brandName ? `Hi team ${config.brandName}.` : "Hello."}
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {config.intro || defaultBrief.intro}
                </motion.p>
              </div>
            )}

            <Section 
              section={currentSectionData}
              answers={data.answers}
              updateAnswer={(id, val) => updateData(`answers.${id}`, val)}
              isActive={true}
              onNext={handleNext}
              onBack={handleBack}
              isFirst={currentSection === 0}
              isLast={currentSection === sections.length - 1}
              validationErrors={validationErrors}
            />

            <div style={{ marginTop: 40, textAlign: 'center' }}>
              <button 
                className="brief-footer-link" 
                onClick={() => generatePdf(defaultBrief, data.answers, config.brandName)}
              >
                Download what you have so far
              </button>
              <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                About {Math.max(1, Math.round((sections.length - currentSection) * 1.5))} minutes left.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSaving && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="brief-autosave"
          >
            Saved
          </motion.div>
        )}
      </AnimatePresence>

      {/* Honeypot */}
      <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
        <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
      </div>
    </div>
  );
}
